// @ts-strict-ignore

import {BehaviorsRequest, BehaviorsResultsMap} from 'app_api/projects/behaviors/types';
import {Metric, MetricsRequest, MetricsResultsMap} from 'app_api/projects/metrics/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {LocalizationStore} from 'common/i18n/store';
import {EntryRowProps, UpdateEntry} from 'common/query-builder/query-entry-section/row-types/types';
import {
  Action,
  MetricEntry,
  MetricEntryBehavior,
  QuerySavedMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {FeatureGate} from 'common/report/constants';
import {BehaviorShowClause, MetricBehavior, MetricMeasurement} from 'common/report/insights/util/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {showMetricsApiErrorToast} from 'common/report/queries/app-api/metrics';
import {getProjectId} from 'common/report/util';
import {buildCreateEntityModalAttrs} from 'common/report/util/saved-entities';
import {Entity, EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {promptSharing} from 'common/widgets/sharing';

import {transformMetricEntryBehaviorToMetricBehavior} from './metric-entry-util';

type UpsertResults = {
  isCreating: boolean;
  results: BehaviorsResultsMap;
};

type UpsertMetricResults = {
  isCreating: boolean;
  results: MetricsResultsMap;
};

export type EditableState = {
  showEditControls: true;
  canEdit: boolean;
  canSave: boolean;
  isSaved: boolean;
  isSaving: boolean;
  saveDisabledMessage: string;
};

export type EditState =
  | EditableState
  | {
      showEditControls: false;
      saveDisabledMessage: string;
    };

export function getTrackingProps(entry: MetricEntry) {
  return {
    id: entry.behavior.id,
    type: entry.behavior.type,
    'is expanded': entry.isExpanded,
    'is hidden': entry.isHidden,
    'type of metrics': Array.from(new Set(entry.behavior.behaviors?.map((behavior) => behavior.type))),
  };
}

export function getAllSavedBehaviors({behaviors, metricType}: {behaviors: BehaviorsResource; metricType: MetricType}) {
  return (
    behaviors
      .getAllValues()
      // fresh deleted behaviors result in `null` in lexicon
      .filter(Boolean)
      .filter((metric) => metric.type === metricType && metric.can_view)
  );
}

export async function saveBehavior({
  behavior,
  currentProject,
  behaviors,
  i18n,
  saveAsNew = false,
  previouslyExisting = false,
}: {
  behavior: MetricEntryBehavior;
  currentProject: CurrentProjectResource;
  behaviors: BehaviorsResource;
  i18n: LocalizationStore;
  saveAsNew: boolean;
  previouslyExisting: boolean;
}) {
  const {isCreating, results} = saveAsNew
    ? await createNewBehavior({behavior, currentProject, behaviors, i18n, previouslyExisting})
    : await updateExistingBehavior({behavior, behaviors});

  // created or updated
  if (results) {
    behaviors.update(results, {partialUpdate: false});
  }

  return {isCreating, results};
}

/** @todo(metrics) marked for deletion */
export const updateMetric = async (
  metricEntity: MetricsRequest,
  metricEntityId: number,
  metrics: MetricsResource,
): Promise<UpsertMetricResults> => {
  let results: MetricsResultsMap;
  try {
    // @ts-ignore
    results = await metrics.patch(metricEntityId, metricEntity);
  } catch {
    showMetricsApiErrorToast();
  }
  return {isCreating: false, results};
};

export async function saveMetric({
  metricEntity,
  currentProject,
  metrics,
  i18n,
  metricEntityId,
}: {
  metricEntity: MetricsRequest;
  currentProject: CurrentProjectResource;
  metrics: MetricsResource;
  i18n: LocalizationStore;
  metricEntityId?: number;
}) {
  const {isCreating, results} = !metricEntityId
    ? await createNewMetric({metricEntity, currentProject, metrics, i18n})
    : await updateMetric(metricEntity, metricEntityId, metrics);

  if (results) {
    metrics.update(results, {partialUpdate: false});
  }

  return {isCreating, results};
}

async function createNewBehavior({
  behavior,
  currentProject,
  behaviors,
  i18n,
  previouslyExisting,
}: {
  behavior: MetricEntryBehavior;
  currentProject: CurrentProjectResource;
  behaviors: BehaviorsResource;
  i18n: LocalizationStore;
  previouslyExisting: boolean;
}): Promise<UpsertResults> {
  const type = behavior.type === MetricType.Funnel ? MetricType.Funnel : MetricType.Retention;

  const {id, name: rawName, ...rawBehavior} = behavior;
  const bookmarkBehavior = transformMetricEntryBehaviorToMetricBehavior(rawBehavior);
  const {name: savedName, filters: _filters, ...behaviorDefinition} = bookmarkBehavior;
  const name = savedName || rawName;
  const newName = previouslyExisting ? `${name} (copy)` : `${name}`;
  const behaviorEntity: BehaviorsRequest = {
    type,
    name: newName,
    description: ``,
    definition: {
      behavior: behaviorDefinition,
    },
  };

  const result = await createEntityModalPromise(
    buildCreateEntityModalAttrs({
      projectId: parseInt(getProjectId(), 10),
      entityType: EntityType.Behavior,
      modalOpen: true,
      isEditing: false,
      isDuplicating: false,
      entity: behaviorEntity as Entity,
      showShareManagement: currentProject.get()?.value?.getFeatureGate(FeatureGate.Sharing),
      modalTitleOverride: i18n.t(`components.create-entity.save-behavior`, {context: type}),
      i18n,
    }),
    {
      existingEntityNames: behaviors.getAllValues().map((value) => value.name),
    },
  );

  if (result.didSave) {
    let results: BehaviorsResultsMap;
    try {
      results = await behaviors.create({
        ...behaviorEntity,
        name: result.name,
        description: result.description,
      });
    } catch {
      results = null;
      addToast({
        message: `Sorry, something went wrong. Please contact support if this problem persists.`,
        type: ToastType.ERROR,
        timeout: null,
      });
    }

    if (result.didShare) {
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: Number(Object.keys(results)[0]),
        'project-id': Number(getProjectId()),
      });
    }
    return {
      isCreating: true,
      results,
    };
  }
  return {
    isCreating: false,
    results: null,
  };
}

export async function createNewMetric({
  metricEntity,
  currentProject,
  metrics,
  i18n,
}: {
  metricEntity: MetricsRequest;
  currentProject: CurrentProjectResource;
  metrics: MetricsResource;
  i18n: LocalizationStore;
}): Promise<UpsertMetricResults> {
  const result = await createEntityModalPromise(
    buildCreateEntityModalAttrs({
      projectId: parseInt(getProjectId(), 10),
      entityType: EntityType.Metric,
      modalOpen: true,
      isEditing: false,
      isDuplicating: false,
      entity: metricEntity,
      showShareManagement: currentProject.get()?.value?.getFeatureGate(FeatureGate.Sharing),
      i18n,
    }),
    {
      existingEntityNames: metrics.getAllValues().map((value) => value.name),
    },
  );

  let results;

  if (result.didSave) {
    try {
      results = await metrics.create({
        ...metricEntity,
        name: result.name,
        description: result.description,
      });
    } catch {
      showMetricsApiErrorToast();
    }
    if (result.didShare) {
      await promptSharing({
        type: SharableEntityType.Metric,
        id: Number(Object.keys(results)[0]),
        'project-id': Number(getProjectId()),
      });
    }
    return {
      isCreating: true,
      results,
    };
  }
  return {
    isCreating: false,
    results,
  };
}

export const handleSelectSavedMetric = (
  metric: Metric,
  updateEntry: UpdateEntry,
  {isSaveExisting = false, isExpanded = false}: {isSaveExisting?: boolean; isExpanded?: boolean} = {},
) => {
  const savedMetricItem: BehaviorShowClause = {
    id: metric.id,
    name: metric.name,
    type: TopLevelMetricType.Metric,
    behavior: {} as MetricBehavior,
    measurement: {} as MetricMeasurement,
  };
  const entry: QuerySavedMetricEntry = {
    name: metric.name,
    type: MetricType.SavedMetric,
    event: savedMetricItem,
    isExpanded,
  };

  updateEntry(isSaveExisting ? Action.SaveMetric : Action.Edit, entry, {
    requery: true,
  });
};

export const handleSaveMetric = async (
  hasFirstClassMetrics: boolean,
  metricEntity: MetricsRequest,
  updateEntry: UpdateEntry,
  currentProject: CurrentProjectResource,
  metrics: MetricsResource,
  i18n: LocalizationStore,
  metricEntityId?: number,
) => {
  if (hasFirstClassMetrics) {
    const {results} = await saveMetric({metricEntity, currentProject, metrics, i18n, metricEntityId});
    const newMetric = Object.values(results)[0] as Metric;
    if (newMetric) {
      handleSelectSavedMetric(newMetric, updateEntry, {isSaveExisting: Boolean(metricEntityId)});
    }
  }
};

async function updateExistingBehavior({
  behavior,
  behaviors,
}: {
  behavior: MetricEntryBehavior;
  behaviors: BehaviorsResource;
}): Promise<UpsertResults> {
  const type = behavior.type === MetricType.Funnel ? MetricType.Funnel : MetricType.Retention;
  let results: BehaviorsResultsMap;
  const {id, ...rawBehavior} = behavior;
  const bookmarkBehavior = transformMetricEntryBehaviorToMetricBehavior(rawBehavior);
  const {name, filters: _filters, ...behaviorDefinition} = bookmarkBehavior;
  try {
    results = await behaviors.patch(id, {
      type,
      id,
      name: behavior.name,
      definition: {
        behavior: behaviorDefinition,
      },
    });
  } catch {
    results = null;
    addToast({
      message: `Sorry, something went wrong. Please contact support if this problem persists.`,
      type: ToastType.ERROR,
      timeout: null,
    });
  }

  if (results) {
    return {
      isCreating: false,
      results,
    };
  }
  return {
    isCreating: false,
    results: null,
  };
}

// Simple validation for the current codebase
export function hasValidBehavior(behavior: MetricEntryBehavior) {
  const behaviors = behavior.behaviors || [];
  const hasValidBehaviors = behaviors.every((beh) => hasValidBehavior(beh));
  if (behavior.type === MetricType.Funnel) {
    return behaviors.length >= 2 && hasValidBehaviors;
  }
  if (behavior.type === MetricType.Retention) {
    return behaviors.length === 2 && hasValidBehaviors;
  }
  const filters = behavior.filters || [];
  return !!behavior.name && filters.every((filter) => new PropertyFilter(filter).isValid());
}

// should probably rename this file from "behavior-utils" since utils use other metric fields
/** helper to extract the datagroup id for funnel / retention query entry rows based on whether metric-level datagroups is enabled. can be removed once GA */
export function getFunnelOrRetentionDataGroupId({
  props,
}: {
  props: Pick<EntryRowProps<MetricEntry>, `entry` | `sectionProps`>;
}) {
  // this shouldn't be needed once metric-level data groups are implemented
  return props.sectionProps.metricLevelDataGroups
    ? props.entry.measurement.dataGroupId
    : props.sectionProps.dataGroupId;
}
